import React, { useState, useEffect } from "react";
import { Plus, Store, Check, AlertTriangle, ShieldCheck } from "lucide-react";
import { Modal } from "../../components/common/Modal";
import { useApp } from "../../app/context/AppContext";
import { useNotification } from "../../app/context/NotificationContext";

export function GRNModal({ isOpen, onClose, defaultPoId }) {
  const { purchaseOrders, warehouses, receiveGRN } = useApp();
  const { addToast } = useNotification();

  const [selectedPoId, setSelectedPoId] = useState(defaultPoId || purchaseOrders[0]?.id || "");
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || "");
  const [inspector, setInspector] = useState("Vikas Raut (Receiving Officer)");
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));

  const targetPO = purchaseOrders.find((p) => p.id === selectedPoId) || purchaseOrders[0];

  const [grnLines, setGrnLines] = useState([]);

  useEffect(() => {
    if (defaultPoId) {
      setSelectedPoId(defaultPoId);
    }
  }, [defaultPoId]);

  useEffect(() => {
    if (targetPO && targetPO.items) {
      setWarehouseId(targetPO.warehouseId || warehouses[0]?.id);
      setGrnLines(
        targetPO.items.map((item, idx) => ({
          productId: item.productId,
          productName: item.productName,
          orderedQty: item.orderedQty,
          receivedQty: Math.max(0, item.orderedQty - (item.receivedQty || 0)),
          damagedQty: 0,
          rejectedQty: 0,
          batchNo: `BAT-${Date.now().toString().slice(-4)}-${idx + 1}`,
          mfgDate: "2026-08-01",
          expiryDate: "2027-08-01",
          destinationBin: "A1-S1-B1",
          unitPrice: item.unitPrice,
          mrp: item.mrp,
          remarks: "Standard inspection ok"
        }))
      );
    }
  }, [targetPO]);

  const updateLine = (index, key, val) => {
    const updated = [...grnLines];
    updated[index][key] = val;
    setGrnLines(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await receiveGRN({
        poId: targetPO.id,
        poNumber: targetPO.poNumber,
        supplierName: targetPO.supplierName,
        warehouseId,
        inspector,
        receivedDate,
        items: grnLines
      });
      addToast(`GRN successfully verified and stock placed in warehouse bins!`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to process GRN putaway.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Goods Receipt Note (GRN) & Bin Putaway"
      subtitle="Inspect incoming shipment, record shortages/damages, assign batch code & target bin"
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {/* Header PO Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Select Purchase Order *
            </label>
            <select
              value={selectedPoId}
              onChange={(e) => setSelectedPoId(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
            >
              {purchaseOrders
                .filter((p) => p.status !== "Closed")
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.poNumber} - {p.supplierName}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Destination Warehouse *
            </label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Receiving Inspector *
            </label>
            <input
              type="text"
              value={inspector}
              onChange={(e) => setInspector(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
            />
          </div>
        </div>

        {/* Lines Inspection & Putaway Grid */}
        <div className="space-y-3">
          <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            Physical Verification & Batch / Shelf Assignment
          </h4>

          <div className="space-y-3">
            {grnLines.map((line, idx) => {
              const shortage = line.orderedQty - Number(line.receivedQty || 0);
              return (
                <div
                  key={idx}
                  className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-2 shadow-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <p className="font-bold text-slate-900 dark:text-white text-xs">
                      {line.productName}
                    </p>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-slate-500">Ordered: {line.orderedQty}</span>
                      {shortage > 0 && (
                        <span className="bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 px-2 py-0.5 rounded font-semibold font-mono">
                          Shortage: {shortage} units
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-400 font-medium">Received Qty</label>
                      <input
                        type="number"
                        value={line.receivedQty}
                        onChange={(e) => updateLine(idx, "receivedQty", Number(e.target.value))}
                        className="w-full px-2 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-rose-500 font-medium">Damaged Qty</label>
                      <input
                        type="number"
                        value={line.damagedQty}
                        onChange={(e) => updateLine(idx, "damagedQty", Number(e.target.value))}
                        className="w-full px-2 py-1 bg-rose-50/50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-md font-mono text-rose-600"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 font-medium">Batch Number</label>
                      <input
                        type="text"
                        value={line.batchNo}
                        onChange={(e) => updateLine(idx, "batchNo", e.target.value)}
                        className="w-full px-2 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md font-mono uppercase"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 font-medium">Expiry Date</label>
                      <input
                        type="date"
                        value={line.expiryDate}
                        onChange={(e) => updateLine(idx, "expiryDate", e.target.value)}
                        className="w-full px-2 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                        Destination Bin (Shelf Location) *
                      </label>
                      <select
                        value={line.destinationBin}
                        onChange={(e) => updateLine(idx, "destinationBin", e.target.value)}
                        className="w-full px-2 py-1 bg-emerald-50/30 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-md font-mono text-emerald-700 dark:text-emerald-300 font-semibold"
                      >
                        <option value="A1-S1-B1">Bin A1-S1-B1 (Section A / Pallet)</option>
                        <option value="A1-S1-B2">Bin A1-S1-B2 (Section A / Grains)</option>
                        <option value="A1-S2-B1">Bin A1-S2-B1 (Section A / Atta Shelf)</option>
                        <option value="B1-S1-B1">Bin B1-S1-B1 (Section B / Oil Drums)</option>
                        <option value="B1-S1-B2">Bin B1-S1-B2 (Section B / Ghee Tins)</option>
                        <option value="C1-S1-B1">Bin C1-S1-B1 (Section C / Snacks)</option>
                        <option value="K-A1-B1">Bin K-A1-B1 (Kurla Depot Shelf)</option>
                        <option value="D-A1-B1">Bin D-A1-B1 (Dadar Counter Godown)</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Controls */}
        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Commit GRN & Putaway to Bins
          </button>
        </div>
      </form>
    </Modal>
  );
}
